import * as CryptoJS from 'crypto-js';

import type { UserI } from '@/pages/Status/Login/interface/UserInterface.interface';

// Get encryption key from environment variables
const secureKey = import.meta.env.VITE_APP_SECRET as string;

// Validate encryption key at startup
if (!secureKey) {
  throw new Error('Encryption key is not defined');
}

/**
 * Quickly check if user data exists without decryption
 * This is used for fast path decisions in the auth flow
 */
export function hasUserData(): boolean {
  return !!localStorage.getItem('u_p');
}

/**
 * Encrypt and store user data in localStorage
 * @param userData - User data to encrypt and store
 */
export function storeUserData(userData: UserI): void {
  try {
    const encryptedData = CryptoJS.AES.encrypt(
      JSON.stringify(userData),
      secureKey,
    ).toString();

    localStorage.setItem('u_p', encryptedData);
  } catch (error) {
    console.error('Failed to encrypt and store user data:', error);
    // Re-throw to allow calling code to handle the error
    throw new Error('Authentication storage error');
  }
}

/**
 * Synchronously retrieve and decrypt user data
 * @returns Decrypted user data or null if not found/error
 */
export function getUserData(): UserI | null {
  try {
    const encryptedData = localStorage.getItem('u_p');
    if (!encryptedData) return null;

    const decryptedData = CryptoJS.AES.decrypt(
      encryptedData,
      secureKey,
    ).toString(CryptoJS.enc.Utf8);

    if (!decryptedData) return null;

    return JSON.parse(decryptedData);
  } catch (error) {
    console.error('Error decrypting user data:', error);
    return null;
  }
}

/**
 * Remove stored user data
 */
export function clearUserData(): void {
  localStorage.removeItem('u_p');
}

/**
 * Asynchronously retrieve and decrypt user data
 * Uses microtask scheduling to avoid blocking the main thread
 * @returns Promise resolving to user data or null
 */
export function getUserDataAsync(): Promise<UserI | null> {
  return new Promise((resolve) => {
    // Use queueMicrotask for better performance than setTimeout
    queueMicrotask(() => {
      try {
        const userData = getUserData();
        resolve(userData);
      } catch (error) {
        console.error('Error in async user data retrieval:', error);
        resolve(null);
      }
    });
  });
}
